import { useEffect, useState } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { ChevronDown, ChevronUp } from 'lucide-react';

import { Checkbox } from 'components/Checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';

import { QuickQuoteInputs } from '../../types';

const SHIPMENT_TOTALS_FIELDS = [
  {
    name: 'ltl.shipmentTotals.wtGrsTot' as const,
    label: 'Wt Grs Tot',
    inlineDimensionUnit: 'lb' as const,
  },
  {
    name: 'ltl.shipmentTotals.wtNetTot' as const,
    label: 'Wt Net Tot',
    inlineDimensionUnit: 'lb' as const,
  },
  {
    name: 'ltl.shipmentTotals.volGrsTot' as const,
    label: 'Vol Grs Tot',
    inlineDimensionUnit: 'CuFt' as const,
  },
  {
    name: 'ltl.shipmentTotals.volNetTot' as const,
    label: 'Vol Net Tot',
    inlineDimensionUnit: 'CuFt' as const,
  },
  { name: 'ltl.shipmentTotals.pieceTot' as const, label: 'Piece Tot' },
  {
    name: 'ltl.shipmentTotals.handlingUnits' as const,
    label: 'Handling Units',
  },
  {
    name: 'ltl.shipmentTotals.linearLengthTot' as const,
    label: 'Linear Length Tot',
    inlineDimensionUnit: 'Ft' as const,
  },
  {
    name: 'ltl.shipmentTotals.billableWeight' as const,
    label: 'Billable Weight',
    inlineDimensionUnit: 'lb' as const,
  },
];

interface ShipmentTotalsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
}

export function ShipmentTotalsSection({
  formMethods,
}: ShipmentTotalsSectionProps) {
  const { control, watch, setValue } = formMethods;
  const [isOpen, setIsOpen] = useState(false);
  const ltl = useWatch({ control, name: 'ltl' });
  const shipmentTotals = useWatch({ control, name: 'ltl.shipmentTotals' });
  const linesOverride = watch('ltl.shipmentTotals.linesOverride') ?? false;
  const readOnly = !linesOverride;

  useEffect(() => {
    if (!ltl) {
      return;
    }

    if (shipmentTotals === undefined) {
      setValue('ltl.shipmentTotals', { linesOverride: false });
    }
  }, [ltl, shipmentTotals, setValue]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className='w-full'>
      <CollapsibleTrigger type='button' className='w-full cursor-pointer py-2'>
        <Flex align='center' justify='between' className='w-full'>
          <Typography
            variant='h5'
            className='text-neutral-800 font-semibold whitespace-nowrap'
          >
            Shipment Totals
          </Typography>
          {isOpen ? (
            <ChevronUp className='h-4 w-4 text-neutral-500' />
          ) : (
            <ChevronDown className='h-4 w-4 text-neutral-500' />
          )}
        </Flex>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Flex direction='col' gap='lg' className='w-full mx-0 pt-2'>
          <div className='mb-1'>
            <Controller
              name='ltl.shipmentTotals.linesOverride'
              control={control}
              render={({ field }) => (
                <Checkbox
                  label='Lines Override'
                  labelClassName='leading-none text-neutral-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                  checked={field.value ?? false}
                  onCheckedChange={(checked) => field.onChange(checked)}
                />
              )}
            />
          </div>

          <Grid cols='2' gap='lg' className='w-full mx-0'>
            {SHIPMENT_TOTALS_FIELDS.map(
              ({ name, label, inlineDimensionUnit }) => (
                <RHFTextInput
                  key={name}
                  name={name}
                  label={label}
                  placeholder='0'
                  inputType='number'
                  options={{ valueAsNumber: true, min: 0 }}
                  required={false}
                  readOnly={readOnly}
                  step={1}
                  inlineDimensionUnit={inlineDimensionUnit}
                />
              )
            )}
          </Grid>
        </Flex>
      </CollapsibleContent>
    </Collapsible>
  );
}
